import type { ItineraryItem } from "@/components/quote/ActivityCard";

export const DEFAULT_ITINERARY_SECTIONS = [
  "Accommodation",
  "Academic & Ongoing Programmes",
  "Workshops",
  "Excursions",
  "Key Transfers",
  "Optional Additional Activities",
];

export type ItineraryLayout = "day_by_day" | "sections";

export function parseItinerarySections(value: unknown): string[] {
  const names = Array.isArray(value) ? value : [];
  return names
    .filter((name): name is string => typeof name === "string" && name.trim().length > 0)
    .map((name) => name.trim());
}

export function defaultSectionForItem(item: Partial<ItineraryItem>): string {
  if (item.item_type === "accommodation") return "Accommodation";
  if (item.item_type === "transfer" || item.item_type === "flight") return "Key Transfers";
  if ((item.session_count || 0) > 1 || item.item_type === "language_excursions") return "Academic & Ongoing Programmes";
  return "Excursions";
}

export function groupItemsBySection<T extends Partial<ItineraryItem>>(
  items: T[],
  configuredSections: unknown,
) {
  const configured = parseItinerarySections(configuredSections);
  const discovered = items.map((item) => item.section_name?.trim()).filter((name): name is string => !!name);
  const names = [...new Set([...configured, ...discovered, ...DEFAULT_ITINERARY_SECTIONS])];
  const grouped = new Map<string, T[]>();
  for (const item of items) {
    const name = item.section_name?.trim() || defaultSectionForItem(item);
    grouped.set(name, [...(grouped.get(name) || []), item]);
  }
  return names
    .filter((name) => grouped.has(name))
    .map((name) => ({ name, items: grouped.get(name) || [] }));
}

export function programmePeriod(start?: string | null, end?: string | null): string | null {
  if (!start) return null;
  const formatDate = (value: string, includeYear: boolean) => {
    const date = new Date(`${value.slice(0, 10)}T00:00:00Z`);
    return new Intl.DateTimeFormat("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: includeYear ? "numeric" : undefined,
      timeZone: "UTC",
    }).format(date);
  };
  if (!end || end === start) return formatDate(start, true);
  const sameYear = start.slice(0, 4) === end.slice(0, 4);
  return `${formatDate(start, !sameYear)} – ${formatDate(end, true)}`;
}